import React, { useState, useEffect } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Container,
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Lock, DesktopAccessDisabled } from '@mui/icons-material';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import ConsultantDashboard from './components/ConsultantDashboard';
import AdminDashboard from './components/AdminDashboard';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Navbar from './components/Navbar';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import telemetryService from './telemetry/telemetryService';
import { authFetch, markSessionReady } from './api/authFetch';

import { TooltipProvider } from './contexts/TooltipContext';

// Portal imports
import PortalGuard, { getDefaultPortal } from './portals/shared/PortalGuard';
import ClientLayout from './portals/client/ClientLayout';
import ClientDashboard from './portals/client/pages/Dashboard';
import ClientAssessments from './portals/client/pages/Assessments';
import ClientAssetLibrary from './portals/client/pages/AssetLibrary';
import ClientPlaybackSessions from './portals/client/pages/PlaybackSessions';
import ClientReports from './portals/client/pages/Reports';
import ClientEraAgent from './portals/client/pages/EraAgent';
import ClientAssessmentRequests from './portals/client/pages/AssessmentRequests';
import ClientRunAssessment from './portals/client/pages/RunAssessment';
import SMELayout from './portals/sme/SMELayout';
import SMEDashboard from './portals/sme/pages/Dashboard';
import SMEDraftReports from './portals/sme/pages/DraftReports';
import SMEPlaybackFeedback from './portals/sme/pages/PlaybackFeedback';
import SMEFinalReports from './portals/sme/pages/FinalReports';
import SMEUploadAssets from './portals/sme/pages/UploadAssets';
import SMEAssignedDomains from './portals/sme/pages/AssignedDomains';
import EMLayout from './portals/em/EMLayout';
import EMDashboard from './portals/em/pages/Dashboard';
import EMAssessmentDomains from './portals/sme/pages/ClientDomains';
import EMAssessmentRequests from './portals/em/pages/AssessmentRequests';
import EMUploadedAssets from './portals/em/pages/UploadedAssets';
import EMValidationQueue from './portals/em/pages/ValidationQueue';
import EMClientFeedback from './portals/em/pages/ClientFeedback';
import ManageDomains from './portals/shared/ManageDomains';
import AIAgentWorkflow from './portals/shared/AIAgentCollaboration';
import AdminLayout from './portals/admin/AdminLayout';
import AdminDashboardPage from './portals/admin/pages/Dashboard';
import AdminKnowledgeBase from './portals/admin/pages/KnowledgeBase';
import AdminDataLifecycle from './portals/admin/pages/DataLifecycle';
import AdminRoleManagement from './portals/admin/pages/RoleManagement';
import AdminSMEManagement from './portals/admin/pages/SMEManagement';
import AdminClientAccess from './portals/admin/pages/ClientAccess';
import AdminAssetManagement from './portals/admin/pages/AssetManagement';
import DomainManagementLayout from './portals/domain-management/DomainManagementLayout';
import AssetManagementDashboard from './portals/domain-management/pages/Dashboard';
import PortalUploadAssets from './portals/domain-management/pages/UploadAssets';
import PortalReviewAssets from './portals/domain-management/pages/ReviewAssets';
import PortalClientUploads from './portals/domain-management/pages/ClientUploads';
import EraGenerator from './portals/domain-management/pages/EraGenerator';
import ClientAssetUpload from './portals/client/pages/ClientAssetUpload';

const theme = createTheme({
  palette: {
    primary: {
      main: '#00338D', // KPMG blue
    },
    secondary: {
      main: '#483698', // KPMG purple
    },
  },
});

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <TooltipProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </AuthProvider>
  );
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isEnterpriseUser, timeoutWarning, timeRemaining, extendSession, logout, concurrentSessionDetected } = useAuth();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [roleDisplayName, setRoleDisplayName] = useState<string | null>(null);
  const [secondaryRoles, setSecondaryRoles] = useState<string[]>([]);
  const [roleLoading, setRoleLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Initialize Application Insights tracking
  useEffect(() => {
    telemetryService.trackPageView('Application Load', window.location.href, {
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`
    });
  }, []);

  // Ensure user DB record is created/updated on login
  useEffect(() => {
    if (isAuthenticated && isEnterpriseUser) {
      setRoleLoading(true);
      setProfileError(null);
      authFetch('/api/auth/me')
        .then((res) => {
          if (!res.ok) throw new Error(`Profile request failed (${res.status})`);
          return res.json();
        })
        .then((data) => {
          if (data.user) {
            setUserRole(data.user.role);
            setRoleDisplayName(data.user.role_display_name || data.user.role);
            setSecondaryRoles(data.user.secondary_roles || []);
          } else {
            throw new Error('Profile response missing user data');
          }
          // Store session token for single-session enforcement
          if (data.session_token) {
            localStorage.setItem('session_token', data.session_token);
          }
          markSessionReady();
        })
        .catch((err) => {
          console.error('Profile fetch failed:', err);
          setProfileError(err.message || 'Failed to load user profile');
        })
        .finally(() => setRoleLoading(false));
    } else {
      setRoleLoading(false);
    }
  }, [isAuthenticated, isEnterpriseUser]);

  // Redirect to the user's default portal after first login/role determination.
  // Only fires once per session — when roleLoading transitions to false with a valid role.
  useEffect(() => {
    if (!roleLoading && userRole) {
      const alreadyRedirected = sessionStorage.getItem('portal_redirected');
      if (!alreadyRedirected) {
        sessionStorage.setItem('portal_redirected', '1');
        const dp = getDefaultPortal(userRole, secondaryRoles);
        if (dp && dp !== 'pending') {
          navigate(`/${dp}`, { replace: true });
        }
      }
    }
  }, [roleLoading, userRole, secondaryRoles, navigate]);

  // Show login page when not authenticated
  if (!isAuthenticated || !isEnterpriseUser) {
    return <Login />;
  }

  // Show error with retry when profile fetch fails
  if (!roleLoading && profileError) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', gap: 2, px: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 500, width: '100%' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Unable to load your profile</Typography>
          <Typography variant="body2">{profileError}</Typography>
        </Alert>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Try Again
          </Button>
          <Button variant="outlined" color="error" onClick={() => logout()}>
            Sign Out
          </Button>
        </Box>
      </Box>
    );
  }

  // Wait for role to be determined before rendering portals to prevent unauthorized flash
  if (roleLoading || userRole === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography variant="h6" color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  // Determine default portal based on user role
  const defaultPortal = getDefaultPortal(userRole, secondaryRoles);

  return (
    <>
      <Navbar userRole={userRole} roleDisplayName={roleDisplayName} secondaryRoles={secondaryRoles} />
      <SessionTimeoutWarning
        open={timeoutWarning}
        timeRemaining={timeRemaining}
        onExtendSession={extendSession}
        onLogout={logout}
      />

      {/* Concurrent session dialog — shown when the same account signs in from another device */}
      <Dialog open={concurrentSessionDetected} disableEscapeKeyDown>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DesktopAccessDisabled color="error" />
          Session Ended
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Your account has been signed in from another device or browser.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            For security, only one active session per account is allowed.
            Please sign in again if you wish to continue on this device.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => logout()} sx={{ bgcolor: '#00338D' }}>
            Sign In Again
          </Button>
        </DialogActions>
      </Dialog>

      <Routes>
        {/* Client Portal Routes */}
        <Route path="/client" element={
          <PortalGuard portal="client" userRole={userRole} secondaryRoles={secondaryRoles}>
            <ClientLayout />
          </PortalGuard>
        }>
          <Route index element={<ClientDashboard />} />
          <Route path="assessments" element={<ClientAssessments />} />
          <Route path="run-assessment" element={<ClientRunAssessment />} />
          <Route path="assets" element={<ClientAssetLibrary />} />
          <Route path="playback" element={<ClientPlaybackSessions />} />
          <Route path="reports" element={<ClientReports />} />
          <Route path="era-agent" element={<ClientEraAgent userRole={userRole} />} />
          <Route path="requests" element={<ClientAssessmentRequests />} />
          <Route path="upload-documents" element={<ClientAssetUpload />} />
        </Route>

        {/* SME Portal Routes */}
        <Route path="/sme" element={
          <PortalGuard portal="sme" userRole={userRole} secondaryRoles={secondaryRoles}>
            <SMELayout userRole={userRole} roleDisplayName={roleDisplayName} />
          </PortalGuard>
        }>
          <Route index element={<SMEDashboard />} />
          <Route path="assigned-domains" element={<SMEAssignedDomains />} />
          <Route path="manage-domains" element={<ManageDomains />} />
          <Route path="upload-assets" element={<SMEUploadAssets />} />
          <Route path="playback" element={<SMEPlaybackFeedback />} />
          <Route path="drafts" element={<SMEDraftReports />} />
        </Route>

        {/* EM/Lead Portal Routes */}
        <Route path="/em" element={
          <PortalGuard portal="em" userRole={userRole} secondaryRoles={secondaryRoles}>
            <EMLayout />
          </PortalGuard>
        }>
          <Route index element={<EMDashboard />} />
          <Route path="assessment-domains" element={<EMAssessmentDomains />} />
          <Route path="requests" element={<EMAssessmentRequests />} />
          <Route path="uploaded-assets" element={<EMUploadedAssets />} />
          <Route path="approved-assets" element={<EMUploadedAssets />} />
          <Route path="manage-domains" element={<ManageDomains />} />
          <Route path="validation" element={<EMValidationQueue />} />
          <Route path="feedback" element={<EMClientFeedback />} />
          <Route path="playback" element={<SMEPlaybackFeedback />} />
          <Route path="reports" element={<SMEFinalReports />} />
        </Route>

        {/* Admin Portal Routes */}
        <Route path="/admin" element={
          <PortalGuard portal="admin" userRole={userRole} secondaryRoles={secondaryRoles}>
            <AdminLayout />
          </PortalGuard>
        }>
          <Route index element={<AdminDashboardPage />} />
          <Route path="agent-collaboration" element={<AIAgentWorkflow />} />
          <Route path="assets" element={
            <PortalGuard portal="domain_management" userRole={userRole} secondaryRoles={secondaryRoles}>
              <AdminAssetManagement />
            </PortalGuard>
          } />
          <Route path="roles" element={<AdminRoleManagement />} />
          <Route path="smes" element={<AdminSMEManagement />} />
          <Route path="clients" element={<AdminClientAccess />} />
          <Route path="knowledge-base" element={<AdminKnowledgeBase />} />
          <Route path="data-lifecycle" element={<AdminDataLifecycle />} />
        </Route>

        {/* Domain Management Portal Routes */}
        <Route path="/domain-management" element={
          <PortalGuard portal="domain_management" userRole={userRole} secondaryRoles={secondaryRoles}>
            <DomainManagementLayout />
          </PortalGuard>
        }>
          <Route index element={<AssetManagementDashboard />} />
          <Route path="manage-domains" element={<ManageDomains />} />
          <Route path="upload-assets" element={<PortalUploadAssets />} />
          <Route path="review-assets" element={<PortalReviewAssets />} />
          <Route path="client-uploads" element={<PortalClientUploads />} />
          <Route path="era-generator" element={<EraGenerator />} />
        </Route>

        {/* Legacy Admin View */}
        <Route path="/legacy-admin" element={
          <Container maxWidth="lg">
            <Box sx={{ py: 4 }}>
              {userRole === 'super_admin' || userRole === 'user_admin' ? (
                <AdminDashboard />
              ) : (
                <Paper elevation={2} sx={{ p: 6, textAlign: 'center' }}>
                  <Lock sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Admin Access Required
                  </Typography>
                  <Alert severity="warning" sx={{ maxWidth: 500, mx: 'auto', mt: 2 }}>
                    Only users with admin permissions can access the admin dashboard.
                  </Alert>
                </Paper>
              )}
            </Box>
          </Container>
        } />

        {/* Legacy Consultant View */}
        <Route path="/legacy-consultant" element={
          <Container maxWidth="lg">
            <Box sx={{ py: 4 }}>
              {userRole === 'super_admin' || userRole === 'engagement_manager' || userRole === 'consultant' || (userRole && (userRole.startsWith('sme_') || userRole.startsWith('em_'))) ? (
                <ConsultantDashboard />
              ) : (
                <Paper elevation={2} sx={{ p: 6, textAlign: 'center' }}>
                  <Lock sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Validation Access Required
                  </Typography>
                  <Alert severity="warning" sx={{ maxWidth: 500, mx: 'auto', mt: 2 }}>
                    Only Engagement Managers, Consultants, and SMEs can access validation.
                  </Alert>
                </Paper>
              )}
            </Box>
          </Container>
        } />

        {/* Default redirect to user's portal */}
        <Route path="/pending" element={
          <Container maxWidth="sm">
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Paper elevation={3} sx={{ p: 6 }}>
                <Lock sx={{ fontSize: 64, color: '#00338D', mb: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Account Pending Approval
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Your KPMG account has been registered but does not yet have portal access.
                  A Super Admin must assign you an appropriate role before you can proceed.
                </Typography>
                <Alert severity="info" sx={{ mt: 2 }}>
                  Please contact your platform administrator to request access.
                </Alert>
              </Paper>
            </Box>
          </Container>
        } />
        <Route path="/" element={<Navigate to={`/${defaultPortal}`} replace />} />
        <Route path="*" element={<Navigate to={`/${defaultPortal}`} replace />} />
      </Routes>
    </>
  );
};

export default App;
